/**
 * EVII guided tour — step model + the three tours.
 *
 * Copy is EVII's voice: short lines, one idea per step. Warren tunes wording
 * before deploy. Anchors are data-tour selectors on real elements (wired in the
 * entry-points commit); a step whose anchor isn't on the current screen renders
 * as a centered explainer card (the overlay degrades gracefully), so the tour
 * always runs even before every anchor exists.
 */

export type TourFlavor = 'public' | 'fleet' | 'multifamily' | 'home';

export interface TourStep {
  /** Stable id — React keys + (later) analytics. */
  id: string;
  /**
   * Route the app should be ON for this step (the tour navigates there when it
   * isn't), so EVII talks about the screen the driver is actually looking at —
   * the map steps show the map, the profile steps show Profile. Absent = stay
   * wherever the previous step left off.
   */
  route?: string;
  /** CSS selector to spotlight; null/absent = centered card, no cutout. */
  anchor?: string | null;
  /**
   * CSS selector the ORB flies to for this step (EVII "touches" the element —
   * e.g. the rewards row). Absent = the orb keeps its progress-interpolated
   * path toward the docked corner.
   */
  orbAnchor?: string;
  /** Window CustomEvent dispatched when this step becomes current (after its
   *  navigation settles). Lets a step open in-page UI the tour can't reach by
   *  route — e.g. the Menu's My Charging bottom sheet. */
  event?: string;
  /** Skip the dim/spotlight for this step so the driver sees the live UI at
   *  full brightness (used when the step opens a sheet to look at). */
  noDim?: boolean;
  /** Pin the card to the top of the viewport instead of the bottom (used when
   *  the interesting UI is a bottom sheet the card would cover). */
  cardAt?: 'top';
  /** EVII's line. Short — one idea per step. */
  title: string;
  /** Optional supporting sentence. */
  body?: string;
  /** Optional hint about what the spotlighted control does. */
  actionHint?: string;
  /**
   * Optional branch: renders choice buttons instead of Next; picking one
   * appends its steps after this one. Drives the multifamily "did your building
   * enable shared charging?" fork.
   */
  choices?: Array<{ label: string; steps: TourStep[] }>;
}

// ── COMMON CORE — all three flavors start here ──────────────────────────────
const CORE: TourStep[] = [
  {
    id: 'core-welcome',
    anchor: null,
    title: 'Great choice. Let me show you around.',
    body: 'This takes about a minute. Tap Next to move along, or Skip whenever you like.',
  },
  {
    id: 'core-footer',
    route: '/map',
    anchor: '[data-tour="footer-nav"]',
    title: 'Everything starts from here.',
    body: 'Find Charger opens the map. Start Charger begins a session. Menu is your account and everything else. These three stay at the bottom.',
  },
  {
    id: 'core-menu',
    route: '/map',
    anchor: '[data-tour="quick-actions"]',
    title: 'Tap the plus for your shortcuts.',
    body: 'Your three shortcuts and me are one tap away on the map. Change them any time from the Menu.',
  },
  {
    id: 'core-scan',
    route: '/map',
    anchor: '[data-tour="tab-scan"]',
    title: 'Starting a charge is easy.',
    body: "Tap Start Charger and scan the QR on the charger. Allow camera access when I ask, point your phone's camera at the code, or just pick the charger's location here and start.",
  },
  {
    id: 'core-profile',
    route: '/profile',
    anchor: null,
    orbAnchor: '[data-tour="rewards-row"]',
    // Card pinned to the top: EVII is pointing at the menu rows mid-screen,
    // so the bottom-anchored card was covering exactly what she's showing.
    cardAt: 'top',
    title: 'Make it yours in the Menu.',
    body: "That's home for your Rewards and points, and where you add a payment card so you're ready to charge.",
  },
  {
    id: 'core-mycharging',
    route: '/profile',
    anchor: null,
    event: 'evi:tour:open-charging-sheet',
    noDim: true,
    cardAt: 'top',
    // Without an orbAnchor the orb's interpolated path parks it right on the
    // top-pinned card at this point in the tour (Warren screenshot). Fly her
    // down to the sheet's rows instead — touching what she's describing.
    orbAnchor: '[data-tour="mycharging-rows"]',
    title: 'My Charging keeps it all together.',
    body: 'Your sessions and history, your chargers, and Friends. Add friends and charge together, share access, and more.',
  },
  {
    id: 'core-themes',
    route: '/themes',
    anchor: null,
    title: 'Give it some personality.',
    body: 'Pick a profile avatar, and open Themes to change how the app looks. New themes unlock the more you charge.',
  },
  {
    id: 'core-help',
    route: '/profile',
    anchor: '[data-tour="evie-launcher"]',
    title: "Stuck? I'm right here.",
    body: 'Tap me anytime for help, or browse step-by-step guides in Help. Ask me anything, anytime.',
  },
];

// ── PUBLIC ──────────────────────────────────────────────────────────────────
const PUBLIC_TAIL: TourStep[] = [
  {
    id: 'public-find',
    route: '/map',
    anchor: null,
    title: 'Find chargers near you.',
    body: 'The map shows what is around you, live. Tap a pin for details, pricing, and to start a session.',
  },
  {
    id: 'public-favorites',
    route: '/favorites',
    anchor: null,
    title: 'Save your regulars.',
    body: 'Star a charger to add it to Favorites, so your go-to spots are always one tap away.',
  },
  {
    id: 'public-coupons',
    route: '/coupons',
    anchor: null,
    title: "Don't miss a deal.",
    body: 'Coupons apply at checkout automatically. You will find yours in the Menu, or pin them to your shortcuts.',
  },
];

// ── FLEET ───────────────────────────────────────────────────────────────────
const FLEET_TAIL: TourStep[] = [
  {
    id: 'fleet-join',
    route: '/profile',
    anchor: null,
    title: 'Join your fleet.',
    body: 'Got an invite code from your fleet manager? Enter it to link up and unlock fleet charging.',
  },
  {
    id: 'fleet-sponsored',
    anchor: null,
    title: 'Your company covers these.',
    body: "Fleet-sponsored sessions are billed to your company, not you. I'll show you which sessions qualify.",
  },
  {
    id: 'fleet-myev',
    anchor: null,
    title: 'Keep an eye on My EV.',
    body: 'Track your vehicle and your fleet charging together in one place.',
  },
];

// ── MULTIFAMILY — core + the shared-charging fork ───────────────────────────
const MULTIFAMILY_TAIL: TourStep[] = [
  {
    id: 'mf-fork',
    anchor: null,
    title: 'One quick question.',
    body: 'Did your building turn on shared charging with EV Initiative?',
    choices: [
      {
        label: "Yes, it's set up",
        steps: [
          {
            id: 'mf-join',
            anchor: null,
            title: "Great. Let's get you on it.",
            body: "Go to Account, then Join a Building, and enter the join code from your property manager. That connects you to your building's chargers.",
          },
        ],
      },
      {
        label: 'Not yet / not sure',
        steps: [
          {
            id: 'mf-ask',
            anchor: null,
            title: "Let's get it started.",
            body: "Ask your property manager about EV Initiative shared charging. It turns your building's chargers into simple, app-based charging for residents. Have them reach us at hello@evinitiative.com and I'll take it from there.",
          },
        ],
      },
    ],
  },
];

// ── HOME CHARGING — where to connect a home charger, ending at My Home ───────
const HOME_TAIL: TourStep[] = [
  {
    id: 'home-menu',
    route: '/profile',
    anchor: null,
    title: 'Home charging starts in the Menu.',
    body: 'Everything for your own charger lives under My Charging in the Menu. Set it up once and manage it here.',
  },
  {
    id: 'home-myhome',
    route: '/my-home',
    anchor: null,
    title: 'This is My Home.',
    body: "Your home charger's dashboard: energy, guests, rates, and access all live here.",
  },
  {
    id: 'home-register',
    route: '/my-chargers/add',
    anchor: null,
    title: 'Connect your charger here.',
    body: 'Have a charger already, or getting one? Register it here to bring it online. I walk you through each step.',
  },
  {
    id: 'home-done',
    route: '/my-home',
    anchor: null,
    title: "You're all set.",
    body: 'Once it is connected, manage your rates, guest access and earnings right here from My Home.',
  },
];

/** Resolve the ordered step list for a chosen driver flavor: COMMON CORE then
 *  the flavor's tail. */
export function getStepsForFlavor(flavor: TourFlavor): TourStep[] {
  const tail =
    flavor === 'public'
      ? PUBLIC_TAIL
      : flavor === 'fleet'
        ? FLEET_TAIL
        : flavor === 'home'
          ? HOME_TAIL
          : MULTIFAMILY_TAIL;
  return [...CORE, ...tail];
}
